import { useCallback, useEffect, useState } from 'react';
import type { CapabilityCode } from '@railroute/contracts/core';
import type {
  RouteSheetBlockingReasonCode,
  RouteSheetForm,
  RouteSheetListResponse,
  RouteSheetStatus,
  RouteSheetView,
} from '@railroute/contracts/rail';
import {
  ApiError,
  fetchCrewCandidates,
  fetchMyRouteSheet,
  fetchRouteSheetForm,
  fetchRouteSheetList,
  fetchWorkerRouteSheet,
  signingAuthorizationUrl,
} from '../api';
import { mayApproveRouteSheet, mayPickRouteSheetWorker } from '../sections';
import { XLSX_MIME, buildXlsx } from '../briefing-journal/xlsx';
import { FIO_MISSING, FORM_COLUMNS, formFileName, formSheet } from '../route-sheets/form';
import { RouteSheetFormSheet } from '../route-sheets/RouteSheetFormSheet';

// Маршрутный лист учёта разъездов за месяц, форма ФРУ № 12 (ADR-008,
// issue #508).
//
// ЛИСТ СОБИРАЕТСЯ СЕРВЕРОМ из подтверждённых поездок: панель не считает ни
// строк, ни номера, ни причин, по которым лист ещё нельзя сдать. Посчитай она
// хоть одно из этого сама — в браузере и на бумаге стояли бы разные листы.
//
// ЧЕЙ ЛИСТ ВИДЕН, РЕШАЕТ СЕРВЕР (`route-sheet-visibility.ts`): своё —
// работник, своих — руководитель, тех, кому выдаёт, — начальник цеха. Выбор
// работника здесь только предлагает спросить; отказ приходит с сервера, а не
// из проверки в панели.
//
// ПЕРЕЧЕНЬ ЛИСТОВ МЕСЯЦА (issue #510): номер, работник, состояние — ровно те
// листы, которые сервер откроет смотрящему (права с охватом).
//
// ИЗ ПОДПИСЕЙ ЗДЕСЬ ОДНА — «Утверждаю к оплате» (ЭЦП, issue #509): её ставит
// руководитель дистанции за компьютером. Выдача, сдача и подтверждение
// подписываются ключом ТЕЛЕФОНА (ADR-008 §7, уровень INTERNAL_DEVICE), а
// такого ключа в браузере не существует.
//
// ВЫГРУЗКА ДЛЯ БУХГАЛТЕРИИ (ADR-008 §9, issue #510): бланк ФРУ № 12 для
// печати и PDF и та же таблица в Excel, графы — из `route-sheets/form.ts`.
// Утверждённый — из подписанного экземпляра; неутверждённый (решение
// владельца по PR #593) — с пометкой «Не утверждён к оплате».
//
// Управление раздела помечено `screen-only`: на бумагу идёт лист, и ничего
// кроме листа. Печатает сам браузер.

/**
 * Работник в перечне выбора — ровно то, чем раздел его называет.
 *
 * Своим типом, а не `SubdivisionMember`: та строка объявлена только в корневом
 * стволе контрактов, а панель читает ствол из ОДНОГО места — `api.ts` (перепись
 * потребителей: `legacy-compatibility-consumer-audit.stage2-8`). Ответ сервера
 * этому типу соответствует, лишние поля ему не мешают.
 */
type SheetWorker = { id: string; name: string; employeeNumber: string };

/** Причины отказа в сдаче — словами, по коду сервера. */
const BLOCKING_REASON_TEXT: Record<RouteSheetBlockingReasonCode, string> = {
  ISSUE_MISSING: 'лист не выдан начальником цеха',
  ROUTE_NOT_FINISHED: 'поездка не завершена',
  ROUTE_NOT_CONFIRMED: 'поездка не принята руководителем',
  MANUAL_MARK_NOT_APPROVED: 'ручная отметка не подтверждена',
};

/** Состояние листа словами (ADR-008 §7). */
const STATUS_TEXT: Record<RouteSheetStatus, string> = {
  OPEN: 'выдан',
  SUBMITTED: 'сдан',
  CONFIRMED: 'подтверждён',
  APPROVED: 'утверждён к оплате',
  RETURNED: 'возвращён на исправление',
};

/** ФИО из карточки; пустое — «ФИО не заполнено», профессию не подставляем. */
function fio(name: string | null | undefined): string {
  const trimmed = name?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : FIO_MISSING;
}

/** Станция с кодом ЕСР — так её узнаёт бухгалтерия (ADR-008 §5). */
function station(name: string | null, esrCode: string | null): string {
  if (!name) return '—';
  return esrCode ? `ст. ${name}, код ${esrCode}` : `ст. ${name}`;
}

/** Предыдущий и следующий месяц строкой `YYYY-MM`. */
function shiftMonth(month: string, delta: number): string {
  const year = Number(month.slice(0, 4));
  const index = Number(month.slice(5, 7)) - 1 + delta;
  const shifted = new Date(Date.UTC(year, index, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function RouteSheetsSection({
  token,
  organizationId,
  capabilities,
  onUnauthorized,
}: {
  token: string;
  organizationId: string;
  capabilities: readonly CapabilityCode[];
  onUnauthorized: () => void;
}) {
  const [sheet, setSheet] = useState<RouteSheetView | null>(null);
  // Месяц не подставляется панелью до первого ответа: текущий считает сервер.
  const [month, setMonth] = useState<string | null>(null);
  // Текущий месяц — тот, который сервер назвал в ответе БЕЗ запрошенного
  // месяца. Дальше него вперёд не ходим: листов там нет, и кнопка, ведущая в
  // пустой экран, читается как поломка.
  const [currentMonth, setCurrentMonth] = useState<string | null>(null);
  const [workerId, setWorkerId] = useState<string>('');
  const [workers, setWorkers] = useState<SheetWorker[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Issue #510: перечень листов месяца и бланк утверждённого листа.
  const [list, setList] = useState<RouteSheetListResponse | null>(null);
  const [form, setForm] = useState<RouteSheetForm | null>(null);
  const [approving, setApproving] = useState(false);

  const mayPickWorker = mayPickRouteSheetWorker(capabilities);
  const mayApprove = mayApproveRouteSheet(capabilities);

  const load = useCallback(
    async (requestedMonth: string | null, requestedWorkerId: string) => {
      setBusy(true);
      setError(null);
      try {
        const next = requestedWorkerId
          ? await fetchWorkerRouteSheet(requestedWorkerId, token, organizationId, requestedMonth ?? undefined)
          : await fetchMyRouteSheet(token, organizationId, requestedMonth ?? undefined);
        setSheet(next);
        setMonth(next.month);
        if (requestedMonth === null) setCurrentMonth(next.month);
      } catch (e) {
        setSheet(null);
        // Истёкший токен — не поломка, а «войдите заново».
        if (e instanceof ApiError && e.status === 401) onUnauthorized();
        setError(e instanceof Error ? e.message : 'Лист не открылся');
      } finally {
        setBusy(false);
      }
    },
    [token, organizationId, onUnauthorized],
  );

  useEffect(() => {
    void load(null, '');
  }, [load]);

  // Перечень листов того же месяца. Отказ по нему не рушит раздел: свой лист
  // читается и без перечня.
  useEffect(() => {
    if (!month) return;
    void fetchRouteSheetList(token, organizationId, month)
      .then(setList)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 401) onUnauthorized();
        setList(null);
      });
  }, [month, token, organizationId, onUnauthorized]);

  /** «Утверждаю к оплате»: подпись ЭЦП общим путём — уход к провайдеру. */
  const approve = useCallback(async () => {
    if (!sheet?.submission || approving) return;
    setApproving(true);
    setError(null);
    try {
      window.location.assign(await signingAuthorizationUrl(sheet.submission.documentId, token, organizationId));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) onUnauthorized();
      setError(e instanceof Error ? e.message : 'Лист не утверждён');
      setApproving(false);
    }
  }, [sheet, approving, token, organizationId, onUnauthorized]);

  /** Бланк ФРУ № 12; неутверждённый — с пометкой «Не утверждён к оплате». */
  const openForm = useCallback(async () => {
    if (!sheet?.id) return;
    setBusy(true);
    setError(null);
    try {
      setForm(await fetchRouteSheetForm(sheet.id, token, organizationId));
    } catch (e) {
      setForm(null);
      if (e instanceof ApiError && e.status === 401) onUnauthorized();
      setError(e instanceof Error ? e.message : 'Форма не открылась');
    } finally {
      setBusy(false);
    }
  }, [sheet, token, organizationId, onUnauthorized]);

  const downloadExcel = () => {
    if (!form) return;
    const widths = FORM_COLUMNS.map((_, index) => (index === 0 ? 6 : index === 9 || index === 11 ? 34 : 16));
    const bytes = buildXlsx('ФРУ № 12', formSheet(form), widths);
    const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: XLSX_MIME }));
    const link = document.createElement('a');
    link.href = url;
    link.download = formFileName(form, 'xlsx');
    link.click();
    URL.revokeObjectURL(url);
  };

  const openFromList = (nextWorkerId: string) => {
    setForm(null);
    setWorkerId(nextWorkerId);
    void load(month, nextWorkerId);
  };

  useEffect(() => {
    if (!mayPickWorker) return;
    // Список людей — то же обращение, которым панель собирает бригаду наряда.
    // Отказ по нему не рушит раздел: свой лист читается и без выбора работника.
    void fetchCrewCandidates(token, organizationId)
      .then(setWorkers)
      .catch(() => setWorkers([]));
  }, [mayPickWorker, token, organizationId]);

  const step = (delta: number) => {
    if (!month) return;
    setForm(null);
    void load(shiftMonth(month, delta), workerId);
  };

  const pickWorker = (next: string) => {
    setForm(null);
    setWorkerId(next);
    void load(month, next);
  };

  return (
    <div className="route-sheets">
      <div className="section__controls screen-only">
        {mayPickWorker ? (
          <label>
            Работник
            <select value={workerId} onChange={(event) => pickWorker(event.target.value)} disabled={busy}>
              <option value="">— мой лист —</option>
              {workers.map((worker) => (
                <option key={worker.id} value={worker.id}>
                  {fio(worker.name)} · таб. {worker.employeeNumber}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <button type="button" onClick={() => step(-1)} disabled={busy || !month}>
          ← предыдущий месяц
        </button>
        <span>{month ?? '—'}</span>
        <button
          type="button"
          onClick={() => step(1)}
          disabled={busy || !month || !currentMonth || month >= currentMonth}
        >
          следующий месяц →
        </button>
      </div>

      {error ? <p className="ui-error panel__error screen-only">{error}</p> : null}
      {busy && !sheet ? <p className="screen-only">Загрузка…</p> : null}

      {list && !form ? (
        <table className="route-sheets__list screen-only">
          <caption>Листы за {list.month}</caption>
          <thead>
            <tr>
              <th>№ листа</th>
              <th>Работник</th>
              <th>Таб. №</th>
              <th>Подразделение</th>
              <th>Состояние</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.sheets.length === 0 ? (
              <tr>
                <td colSpan={6}>Выданных листов за этот месяц нет.</td>
              </tr>
            ) : (
              list.sheets.map((item) => (
                <tr key={item.sheetId}>
                  <td>{item.number ?? '—'}</td>
                  <td>{fio(item.workerName)}</td>
                  <td>{item.workerEmployeeNumber}</td>
                  <td>{item.subdivisionName ?? '—'}</td>
                  <td>{STATUS_TEXT[item.status]}</td>
                  <td>
                    <button type="button" onClick={() => openFromList(item.workerId)} disabled={busy}>
                      Открыть
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      ) : null}

      {form ? (
        <>
          <div className="section__controls screen-only">
            <button type="button" onClick={() => setForm(null)}>
              ← к листу
            </button>
            <button type="button" onClick={() => window.print()}>
              Печать (PDF)
            </button>
            <button type="button" onClick={downloadExcel}>
              Выгрузить в Excel
            </button>
          </div>
          <RouteSheetFormSheet form={form} />
        </>
      ) : null}

      {sheet && !form ? (
        <div className="route-sheets__actions screen-only">
          <span>Состояние: {STATUS_TEXT[sheet.status]}</span>
          {/* «Утверждаю к оплате» — у подтверждённого старшим листа и тому, у
              кого есть право; область решает сервер. */}
          {sheet.status === 'CONFIRMED' && sheet.submission && mayApprove ? (
            <button type="button" onClick={() => void approve()} disabled={approving}>
              {approving ? 'Переход к подписи…' : 'Утверждаю к оплате (ЭЦП)'}
            </button>
          ) : null}
          {/* Выгрузка — у любого выданного листа (решение владельца по PR #593):
              неутверждённый печатается с пометкой «Не утверждён к оплате». */}
          {sheet.id ? (
            <button type="button" onClick={() => void openForm()} disabled={busy}>
              {sheet.status === 'APPROVED'
                ? 'Форма ФРУ № 12 — печать и Excel'
                : 'Форма ФРУ № 12 — печать и Excel (не утверждён к оплате)'}
            </button>
          ) : null}
        </div>
      ) : null}

      {sheet && !form ? (
        <>
          <dl className="route-sheets__head">
            <dt>Номер листа</dt>
            <dd>{sheet.header.number ?? 'не выдан'}</dd>
            <dt>Месяц</dt>
            <dd>{sheet.month}</dd>
            <dt>Организация</dt>
            <dd>
              {sheet.header.organizationName}
              {sheet.header.subdivisionName ? ` · ${sheet.header.subdivisionName}` : ''}
            </dd>
            <dt>Должность</dt>
            <dd>{sheet.header.professionName ?? '—'}</dd>
            <dt>Ф. И. О., табельный</dt>
            <dd>
              {fio(sheet.header.workerName)} · таб. {sheet.header.workerEmployeeNumber}
            </dd>
            <dt>Место приписки</dt>
            <dd>{station(sheet.header.homeStationName, sheet.header.homeStationEsrCode)}</dd>
            <dt>Выдал начальник цеха</dt>
            <dd>
              {sheet.issue?.signed
                ? `${sheet.issue.issuedByName} · таб. ${sheet.issue.issuedByEmployeeNumber}`
                : 'не подписано'}
            </dd>
          </dl>

          {sheet.canSubmit ? (
            <p className="route-sheets__ready">Лист можно сдавать.</p>
          ) : (
            <div className="route-sheets__blocked screen-only">
              <p>Сдать лист пока нельзя:</p>
              <ul>
                {sheet.blockingReasons.map((reason, index) => (
                  <li key={`${reason.code}-${reason.routeId ?? 'sheet'}-${index}`}>
                    {BLOCKING_REASON_TEXT[reason.code]}
                    {reason.stationName ? ` — ст. ${reason.stationName}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <table className="route-sheets__lines">
            <thead>
              <tr>
                <th>№</th>
                <th colSpan={4}>Выбыл</th>
                <th colSpan={4}>Прибыл</th>
                <th>Наименование работ</th>
                <th>Примечание</th>
              </tr>
              <tr>
                <th />
                <th>дата</th>
                <th>время</th>
                <th>№ поезда</th>
                <th>станция</th>
                <th>дата</th>
                <th>время</th>
                <th>№ поезда</th>
                <th>станция</th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {sheet.lines.length === 0 ? (
                <tr>
                  <td colSpan={11}>Подтверждённых поездок за этот месяц нет.</td>
                </tr>
              ) : (
                sheet.lines.map((line) => (
                  <tr key={`${line.routeId}-${line.sequence}`}>
                    <td>{line.sequence}</td>
                    <td>{line.departed.date}</td>
                    <td>{line.departed.time}</td>
                    <td>{line.departed.trainNumber ?? '—'}</td>
                    <td>
                      {station(line.departed.stationName, line.departed.stationEsrCode)}
                      {line.departed.method === 'MANUAL_FALLBACK' ? (
                        <em>
                          {' '}
                          ручная
                          {line.departed.manualApprovedByName
                            ? `, подтвердил ${line.departed.manualApprovedByName}, таб. ${line.departed.manualApprovedByEmployeeNumber ?? '—'}`
                            : ', не подтверждена'}
                        </em>
                      ) : null}
                    </td>
                    <td>{line.arrived.date}</td>
                    <td>{line.arrived.time}</td>
                    <td>{line.arrived.trainNumber ?? '—'}</td>
                    <td>
                      {station(line.arrived.stationName, line.arrived.stationEsrCode)}
                      {line.arrived.method === 'MANUAL_FALLBACK' ? (
                        <em>
                          {' '}
                          ручная
                          {line.arrived.manualApprovedByName
                            ? `, подтвердил ${line.arrived.manualApprovedByName}, таб. ${line.arrived.manualApprovedByEmployeeNumber ?? '—'}`
                            : ', не подтверждена'}
                        </em>
                      ) : null}
                    </td>
                    <td>{line.returnToHome ? 'возвращение к месту приписки' : (line.workName ?? '—')}</td>
                    <td>
                      {line.resultStatus === 'DONE'
                        ? 'выполнено'
                        : line.resultStatus === 'PARTIAL'
                          ? 'выполнено частично'
                          : line.resultStatus === 'NOT_DONE'
                            ? 'не выполнено'
                            : '—'}
                      {line.resultComment ? `: ${line.resultComment}` : ''}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      ) : null}
    </div>
  );
}
